import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormatters } from '../../../hooks';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  Typography,
} from '../../../ui/core';
import { EVENT_ACTORS, EVENT_CATEGORIES, categoryChipColor, toggleValue } from './eventLogFilters';

/**
 * Shape of a trip_ride_event row returned by the admin event-log API.
 *
 * `type` is a machine token sent by the backend (e.g. "driver.engaged",
 * "trip.major_delay") — render it as a small, muted metadata chip, not the
 * headline. `description` is an optional human-readable line that, when
 * present, is the prominent headline of the row; it may be null/absent.
 *
 * `category` (e.g. "lifecycle", "optimizer") and `visibility`
 * ("internal" | "ops" | "participant") drive color-coding and dimming.
 *
 * @typedef {Object} TripRideEvent
 * @property {string} id
 * @property {string} entityType
 * @property {string} [entityId]
 * @property {string} [tripId]
 * @property {string|null} [rideId]
 * @property {string} type
 * @property {string|null} [description]
 * @property {string} [category]
 * @property {string} [visibility]
 * @property {string} occurredAt
 * @property {string} [recordedAt]
 * @property {string} actor
 * @property {string|null} [actorId]
 * @property {string} [source]
 * @property {Object} [payload]
 */

const formatPayloadValue = (value) => {
  if (value === null || value === undefined) return String(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const payloadSummary = (payload) => {
  const entries = Object.entries(payload || {}).slice(0, 3);
  if (entries.length === 0) return null;
  return entries.map(([key, value]) => `${key}: ${formatPayloadValue(value)}`).join(' · ');
};

const EventPayload = ({ payload }) => {
  const [open, setOpen] = useState(false);

  if (!payload || Object.keys(payload).length === 0) {
    return null;
  }

  return (
    <>
      <Button size="small" sx={{ padding: 0, minWidth: 0, textTransform: 'none' }} onClick={() => setOpen(!open)}>
        {open ? 'Hide payload' : 'View payload'}
      </Button>
      {open && (
        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', margin: '4px 0 0 0', fontSize: '12px' }}>
          {JSON.stringify(payload, null, 2)}
        </Box>
      )}
    </>
  );
};

const EntityLabel = ({ event, ridersByRideId }) => {
  if (event.entityType !== 'ride' || !event.rideId) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        trip
      </Typography>
    );
  }

  const riderName = ridersByRideId?.[event.rideId];
  return (
    <Button
      component={Link}
      to={`/rides/${event.rideId}`}
      size="small"
      sx={{ padding: 0, minWidth: 0, textTransform: 'none' }}
    >
      ride{riderName ? ` — ${riderName}` : ''}
    </Button>
  );
};

/**
 * A filter pill that opens its options instead of spreading them across the
 * bar. Eight category chips and four actor chips wrapped onto three rows and
 * cost more vertical space than the events did, which is the wrong trade in a
 * side panel.
 *
 * An empty selection still means "all"; the pill shows the count when it does
 * not.
 */
const FilterMenu = ({ label, options, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const active = selected.length > 0;

  return (
    <>
      <Chip
        label={active ? `${label} · ${selected.length}` : label}
        size="small"
        variant={active ? 'filled' : 'outlined'}
        color={active ? 'primary' : 'default'}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        // Clearing is one click on the X rather than unticking each option.
        onDelete={active ? () => onChange([]) : undefined}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            dense
            data-value={option.value}
            onClick={() => onChange(toggleValue(selected, option.value))}
          >
            <Checkbox size="small" checked={selected.includes(option.value)} sx={{ padding: '4px' }} />
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * Server-side filter bar. Selections are pushed up to the parent (via the
 * `filters` object from `useEventLogFilters`), which refetches with the
 * corresponding `?categories=`, `?actors=`, and `?includeInternal=` params.
 */
const EventLogFilters = ({ filters }) => (
  <Stack
    direction="row"
    spacing="8px"
    alignItems="center"
    flexWrap="wrap"
    useFlexGap
    sx={{ marginTop: '8px', marginBottom: '4px' }}
  >
    <FilterMenu
      label="Category"
      options={EVENT_CATEGORIES}
      selected={filters.categories}
      onChange={filters.setCategories}
    />
    <FilterMenu label="Actor" options={EVENT_ACTORS} selected={filters.actors} onChange={filters.setActors} />
    <FormControlLabel
      // Pushed to the right so it reads as a separate control rather than a
      // third filter pill; `gap` adds room between the switch and its label
      // (FormControlLabel's root is inline-flex).
      sx={{ marginLeft: 'auto', marginRight: 0, gap: '6px' }}
      control={
        // No `size="small"`: the theme already sizes every Switch (26x16 track,
        // 12px thumb), and MUI's small variant layers a 16px thumb on top of
        // that fixed track, so the thumb outgrows it and renders as a blob.
        <Switch
          checked={filters.includeInternal}
          onChange={(event) => filters.setIncludeInternal(event.target.checked)}
        />
      }
      label={
        <Typography variant="body2" color="text.secondary">
          Internal
        </Typography>
      }
    />
  </Stack>
);

/**
 * Shared timeline for trip_ride_event rows, newest first. Used by the ride
 * detail page and the trip detail dialog's Event Log section.
 *
 * Category/actor/visibility filtering is server-side: pass the `filters` object
 * from `useEventLogFilters` to render the filter bar; the parent refetches when
 * a selection changes. Without `filters`, the bar is hidden and all supplied
 * `events` render.
 *
 * `loading` shows the skeleton (initial load). `refetching` keeps the current
 * rows visible under a progress bar while a filter-driven refetch is in flight.
 */
const EventLogTimeline = ({
  events,
  loading,
  refetching = false,
  timezone,
  showEntityColumn = false,
  ridersByRideId,
  filters,
}) => {
  const f = useFormatters();

  if (loading) {
    // Keep the filter bar mounted under the skeleton so a refetch triggered by
    // changing a filter doesn't make the control the user is interacting with
    // disappear until the new results arrive.
    return (
      <Stack>
        {filters && <EventLogFilters filters={filters} />}
        <Stack spacing="8px" sx={{ padding: '8px 0' }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="text" width="55%" />
        </Stack>
      </Stack>
    );
  }

  const isEmpty = !events || events.length === 0;
  const filtersActive =
    filters && (filters.categories.length > 0 || filters.actors.length > 0 || filters.includeInternal);

  const sorted = isEmpty ? [] : [...events].sort((e1, e2) => Date.parse(e2.occurredAt) - Date.parse(e1.occurredAt));

  const formatTime = (iso) =>
    timezone
      ? f.date.toTimezoneLocaleString(iso, timezone, f.date.DateFormats.DATETIME_SIMPLE_WITH_TIMEZONE)
      : f.date.toLocaleString(iso, f.date.DateFormats.DATETIME_MED_WITH_SEC);

  return (
    <Stack>
      {filters && <EventLogFilters filters={filters} />}
      {refetching && <LinearProgress sx={{ marginTop: '4px' }} />}
      {isEmpty ? (
        <Typography variant="body2" color="text.secondary" sx={{ padding: '8px 0' }}>
          {filtersActive
            ? 'No events match the selected filters.'
            : `No events recorded for this ${showEntityColumn ? 'trip' : 'ride'} yet.`}
        </Typography>
      ) : (
        <Timeline sx={{ padding: 0, margin: '8px 0 0 0' }}>
          {sorted.map((event, idx) => {
            const summary = payloadSummary(event.payload);
            const isInternal = event.visibility === 'internal';
            return (
              // Without opposite content MUI still reserves the left half for
              // it, so `::before` has to go or every row sits centred.
              <TimelineItem key={event.id} sx={{ '&::before': { display: 'none' } }}>
                <TimelineSeparator>
                  <TimelineDot />
                  {idx !== sorted.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ opacity: isInternal ? 0.6 : 1 }}>
                  {/* In the content, not a fixed left column: the column cost
                      140px of every row's width and left the text a narrow
                      ribbon in a side panel. */}
                  <Typography variant="caption" color="text.secondary">
                    {formatTime(event.occurredAt)}
                  </Typography>
                  {event.description && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {event.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing="8px" alignItems="center" flexWrap="wrap" sx={{ marginTop: '4px' }}>
                    <Chip
                      label={event.type}
                      size="small"
                      variant="outlined"
                      color={categoryChipColor(event.category)}
                      sx={{ fontFamily: 'monospace', fontSize: '11px' }}
                    />
                    <Chip label={event.actor} size="small" />
                    {isInternal && <Chip label="internal" size="small" variant="outlined" color="warning" />}
                    <Typography variant="body2" color="text.secondary">
                      {event.source}
                    </Typography>
                    {showEntityColumn && <EntityLabel event={event} ridersByRideId={ridersByRideId} />}
                  </Stack>
                  {summary && (
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '4px' }}>
                      {summary}
                    </Typography>
                  )}
                  <EventPayload payload={event.payload} />
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      )}
    </Stack>
  );
};

export default EventLogTimeline;
